/**
 * Gallery Utility Library - Gallery v2
 *
 * Helper functions for album and media management in the unified gallery system.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  Firestore,
  increment,
  writeBatch,
} from 'firebase/firestore';
import { Album, AlbumType, buildAlbumPath, slugify, normalizeAlbumPath } from '@/models/album';
import { MediaItem } from '@/models/media-item';
import { parseFirebaseIndexError } from '@/lib/firebase-index-error';

/**
 * Get or create an album by path
 */
export async function getOrCreateAlbum(
  firestore: Firestore,
  albumPath: string,
  createdBy: string,
  options?: {
    name?: string;
    albumType?: AlbumType;
    parentAlbumId?: string;
    autoDateDetection?: boolean;
    description?: string;
    sortOrder?: number;
    sortName?: string;
  }
): Promise<Album> {
  // Normalize path to handle legacy paths with leading slashes
  const normalizedPath = normalizeAlbumPath(albumPath);

  // Try to find existing album by normalized path OR legacy path with leading slash
  const albumsRef = collection(firestore, 'albums');
  const q = query(albumsRef, where('albumPath', '==', normalizedPath), limit(1));
  let snapshot = await getDocs(q);

  // If not found with normalized path, try with leading slash (legacy format)
  if (snapshot.empty && !albumPath.startsWith('/')) {
    const legacyPath = `/${normalizedPath}`;
    const legacyQ = query(albumsRef, where('albumPath', '==', legacyPath), limit(1));
    snapshot = await getDocs(legacyQ);
  }

  if (!snapshot.empty) {
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Album;
  }

  // Create new album with normalized path
  const albumId = doc(albumsRef).id;
  const pathSegments = normalizedPath.split('/').filter(Boolean);
  const albumName = options?.name || pathSegments[pathSegments.length - 1];

  const newAlbum: Omit<Album, 'id'> = {
    name: albumName,
    slug: slugify(albumName),
    albumPath: normalizedPath,
    description: options?.description || '',
    depth: pathSegments.length - 1,
    sortOrder: options?.sortOrder || 0,
    sortName: options?.sortName || null,
    mediaCount: 0,
    viewCount: 0,
    albumType: options?.albumType || AlbumType.CUSTOM,
    createdBy,
    source: 'firebase',
    visibility: 'members',
    isArchived: false,
    parentId: options?.parentAlbumId || null,
    parentAlbumId: options?.parentAlbumId || null,
    autoDateDetection: options?.autoDateDetection || false,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  await setDoc(doc(firestore, 'albums', albumId), newAlbum);

  return { id: albumId, ...newAlbum };
}

// Helper to get or create year album
async function getOrCreateFieldFlightsYearAlbum(
  firestore: Firestore,
  year: number,
  parentId: string,
  createdBy: string
): Promise<Album> {
  const yearStr = year.toString();
  const yearAlbumPath = buildAlbumPath('field-flights', yearStr);

  return getOrCreateAlbum(firestore, yearAlbumPath, createdBy, {
    name: yearStr,
    albumType: AlbumType.FIELD_FLIGHTS,
    parentAlbumId: parentId,
    description: `Field flights from ${yearStr}`,
    sortOrder: -year, // Newest years first
    autoDateDetection: false,
  });
}

// Helper to get or create month album
async function getOrCreateFieldFlightsMonthAlbum(
  firestore: Firestore,
  year: number,
  monthName: string, // e.g. "February"
  monthIndex: number, // 1-12
  parentId: string,
  createdBy: string
): Promise<Album> {
  const yearStr = year.toString();
  const monthAlbumPath = buildAlbumPath(`field-flights/${yearStr}`, monthName);

  return getOrCreateAlbum(firestore, monthAlbumPath, createdBy, {
    name: monthName,
    albumType: AlbumType.FIELD_FLIGHTS,
    parentAlbumId: parentId,
    description: `Field flights from ${monthName} ${year}`,
    sortOrder: monthIndex,
    autoDateDetection: false,
  });
}

/**
 * Get or create Field Flights date album
 * Example: getOrCreateFieldFlightsAlbum(firestore, '2024-12-25', userId)
 * Structure: field-flights/{year}/{month}/{dateStr}
 */
export async function getOrCreateFieldFlightsAlbum(
  firestore: Firestore,
  dateStr: string, // YYYY-MM-DD format
  createdBy: string
): Promise<Album> {
  // First ensure Field Flights parent album exists
  const fieldFlightsPath = 'field-flights';
  const parentAlbum = await getOrCreateAlbum(
    firestore,
    fieldFlightsPath,
    createdBy,
    {
      name: 'Field Flights',
      albumType: AlbumType.SYSTEM,
      autoDateDetection: true,
    }
  );

  const date = new Date(dateStr);
  const year = date.getFullYear();
  const monthName = date.toLocaleDateString('en-US', { month: 'long' }); // "February"
  const monthIndex = date.getMonth() + 1; // 1-12

  // Create Year Album
  const yearAlbum = await getOrCreateFieldFlightsYearAlbum(firestore, year, parentAlbum.id, createdBy);

  // Create Month Album
  const monthAlbum = await getOrCreateFieldFlightsMonthAlbum(
    firestore,
    year,
    monthName,
    monthIndex,
    yearAlbum.id,
    createdBy
  );

  // Create date-specific album
  // Path: field-flights/{year}/{month}/{dateStr}
  const dateAlbumPath = buildAlbumPath(monthAlbum.albumPath, dateStr);

  // Format name for display (e.g. "Dec 25, 2024")
  const albumName = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return getOrCreateAlbum(firestore, dateAlbumPath, createdBy, {
    name: albumName, // "Dec 25, 2024"
    albumType: AlbumType.FIELD_FLIGHTS,
    parentAlbumId: monthAlbum.id,
    autoDateDetection: false,
    sortName: dateStr.replace(/-/g, ''), // YYYYMMDD for sorting
  });
}

/**
 * Get or create member personal album
 * Example: getOrCreateMemberPersonalAlbum(firestore, userId, 'John Smith')
 */
export async function getOrCreateMemberPersonalAlbum(
  firestore: Firestore,
  ownerId: string,
  ownerName: string
): Promise<Album> {
  // First ensure Members parent album exists
  const membersPath = 'members';
  const parentAlbum = await getOrCreateAlbum(firestore, membersPath, ownerId, {
    name: 'Members',
    albumType: AlbumType.SYSTEM,
  });

  // Check if personal album already exists
  const albumsRef = collection(firestore, 'albums');
  const q = query(
    albumsRef,
    where('albumType', '==', AlbumType.MEMBER_PERSONAL),
    where('ownerId', '==', ownerId),
    limit(1)
  );
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Album;
  }

  // Create personal album
  const personalAlbumPath = buildAlbumPath(membersPath, slugify(ownerName));
  const albumId = doc(albumsRef).id;

  const newAlbum: Omit<Album, 'id'> = {
    name: ownerName,
    slug: slugify(ownerName),
    albumPath: personalAlbumPath,
    depth: 1,
    sortOrder: 0,
    mediaCount: 0,
    viewCount: 0,
    albumType: AlbumType.MEMBER_PERSONAL,
    ownerId,
    ownerName,
    createdBy: ownerId,
    source: 'firebase',
    visibility: 'private', // Personal albums are private by default
    isArchived: false,
    parentId: parentAlbum.id,
    parentAlbumId: parentAlbum.id,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  await setDoc(doc(firestore, 'albums', albumId), newAlbum);

  return { id: albumId, ...newAlbum };
}

/**
 * Update album media count
 */
export async function updateAlbumMediaCount(
  firestore: Firestore,
  albumId: string,
  delta: number
): Promise<void> {
  const albumRef = doc(firestore, 'albums', albumId);
  await updateDoc(albumRef, {
    mediaCount: increment(delta),
    lastMediaAddedAt: delta > 0 ? Timestamp.now() : undefined,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Get album by ID
 */
export async function getAlbumById(
  firestore: Firestore,
  albumId: string
): Promise<Album | null> {
  const albumRef = doc(firestore, 'albums', albumId);
  const albumSnap = await getDoc(albumRef);

  if (!albumSnap.exists()) {
    return null;
  }

  return { id: albumSnap.id, ...albumSnap.data() } as Album;
}

/**
 * Get all root albums (no parent)
 */
export async function getRootAlbums(firestore: Firestore): Promise<Album[]> {
  const albumsRef = collection(firestore, 'albums');
  const q = query(
    albumsRef,
    where('parentAlbumId', '==', null),
    where('isArchived', '==', false),
    orderBy('sortOrder', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Album));
}

/**
 * Get sub-albums of a parent album
 * Field Flights albums are sorted by date (newest first)
 */
export async function getSubAlbums(
  firestore: Firestore,
  parentAlbumId: string
): Promise<Album[]> {
  const albumsRef = collection(firestore, 'albums');

  // Get parent album to check if it's Field Flights
  const parentDoc = await getDoc(doc(firestore, 'albums', parentAlbumId));
  const isFieldFlightsParent = parentDoc.exists() &&
    (parentDoc.data()?.albumType === AlbumType.FIELD_FLIGHTS ||
      parentDoc.data()?.albumType === AlbumType.SYSTEM &&
      parentDoc.data()?.albumPath === 'field-flights');

  // Field Flights sub-albums sort by sortOrder DESC (newest first)
  // Other albums sort by sortOrder ASC (defined order)
  const q = query(
    albumsRef,
    where('parentAlbumId', '==', parentAlbumId),
    where('isArchived', '==', false),
    orderBy('sortOrder', isFieldFlightsParent ? 'desc' : 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Album));
}

/**
 * Get media items in an album
 */
export async function getAlbumMedia(
  firestore: Firestore,
  albumId: string,
  options?: {
    limit?: number;
    sortBy?: 'uploadedAt' | 'viewCount' | 'title';
    sortDir?: 'asc' | 'desc';
  }
): Promise<MediaItem[]> {
  const mediaRef = collection(firestore, 'media');

  const sortField = options?.sortBy || 'uploadedAt';
  const sortDir = options?.sortDir || 'desc';

  const q = query(
    mediaRef,
    where('albumId', '==', albumId),
    orderBy(sortField, sortDir),
    ...(options?.limit ? [limit(options.limit)] : [])
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MediaItem));
}

/**
 * Get media item by ID
 */
export async function getMediaById(
  firestore: Firestore,
  mediaId: string
): Promise<MediaItem | null> {
  const mediaRef = doc(firestore, 'media', mediaId);
  const mediaSnap = await getDoc(mediaRef);

  if (!mediaSnap.exists()) {
    return null;
  }

  return { id: mediaSnap.id, ...mediaSnap.data() } as MediaItem;
}

/**
 * Increment media view count
 */
export async function incrementMediaViewCount(
  firestore: Firestore,
  mediaId: string
): Promise<void> {
  const mediaRef = doc(firestore, 'media', mediaId);
  await updateDoc(mediaRef, {
    viewCount: increment(1),
  });
}

/**
 * Increment album view count
 */
export async function incrementAlbumViewCount(
  firestore: Firestore,
  albumId: string
): Promise<void> {
  const albumRef = doc(firestore, 'albums', albumId);
  await updateDoc(albumRef, {
    viewCount: increment(1),
  });
}

/**
 * Delete media item and update album count
 */
export async function deleteMediaItem(
  firestore: Firestore,
  mediaId: string
): Promise<void> {
  const mediaRef = doc(firestore, 'media', mediaId);
  const mediaSnap = await getDoc(mediaRef);

  if (!mediaSnap.exists()) {
    throw new Error('Media item not found');
  }

  const mediaData = mediaSnap.data() as MediaItem;
  const batch = writeBatch(firestore);

  // Delete media document
  batch.delete(mediaRef);

  // Update album media count
  const albumRef = doc(firestore, 'albums', mediaData.albumId);
  batch.update(albumRef, {
    mediaCount: increment(-1),
    updatedAt: Timestamp.now(),
  });

  await batch.commit();
}

/**
 * Search media by tags
 */
export async function searchMediaByTags(
  firestore: Firestore,
  tags: string[],
  options?: {
    limit?: number;
  }
): Promise<MediaItem[]> {
  const mediaRef = collection(firestore, 'media');

  // Firestore supports array-contains but not array-contains-any for multiple tags
  // For simplicity, search for first tag. For multiple tags, need to use client-side filtering
  const q = query(
    mediaRef,
    where('tags', 'array-contains', tags[0]),
    orderBy('uploadedAt', 'desc'),
    ...(options?.limit ? [limit(options.limit)] : [])
  );

  const snapshot = await getDocs(q);
  let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MediaItem));

  // Client-side filter for additional tags
  if (tags.length > 1) {
    results = results.filter(media =>
      tags.every(tag => media.tags.includes(tag))
    );
  }

  return results;
}

/**
 * Get recent media across all albums
 */
export async function getRecentMedia(
  firestore: Firestore,
  limitCount: number = 20
): Promise<MediaItem[]> {
  const mediaRef = collection(firestore, 'media');
  const q = query(
    mediaRef,
    where('visibility', 'in', ['public', 'members']),
    orderBy('uploadedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MediaItem));
}

/**
 * Extract date from EXIF data or filename
 * Returns YYYY-MM-DD format or null
 */
export function extractDateFromFilename(filename: string): string | null {
  // Common date patterns in filenames
  const patterns = [
    /(\d{4})-(\d{2})-(\d{2})/, // YYYY-MM-DD
    /(\d{4})(\d{2})(\d{2})/, // YYYYMMDD
    /(\d{2})-(\d{2})-(\d{4})/, // MM-DD-YYYY
    /(\d{2})(\d{2})(\d{4})/, // MMDDYYYY
  ];

  for (const pattern of patterns) {
    const match = filename.match(pattern);
    if (match) {
      // Try to construct YYYY-MM-DD
      if (match[1].length === 4) {
        // YYYY-MM-DD or YYYYMMDD
        return `${match[1]}-${match[2]}-${match[3]}`;
      } else {
        // MM-DD-YYYY or MMDDYYYY
        return `${match[3]}-${match[1]}-${match[2]}`;
      }
    }
  }

  return null;
}

/**
 * Format date as YYYY-MM-DD
 */
export function formatDateToString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if date is recent (within last 30 days)
 */
export function isRecentDate(date: Date): boolean {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30;
}

/**
 * Get random media items from the gallery (simple query without composite index)
 * @param excludeIds - Optional array of media IDs to exclude (prevent duplicates)
 */
export async function getRandomMedia(
  firestore: Firestore,
  limitCount: number = 1,
  excludeIds: string[] = []
): Promise<MediaItem[]> {
  const mediaRef = collection(firestore, 'media');
  const excludeSet = new Set(excludeIds);

  try {
    // Simple query - just get photos, then filter in memory
    const randomQuery = query(
      mediaRef,
      where('type', '==', 'photo'),
      limit(100) // Get enough to randomize from after exclusions
    );

    const snapshot = await getDocs(randomQuery);
    let allMedia = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MediaItem));

    // Filter by visibility in memory to avoid composite index
    // Also exclude already-used images
    allMedia = allMedia.filter(m =>
      (m.visibility === 'public' || m.visibility === 'members') &&
      !excludeSet.has(m.id)
    );

    if (allMedia.length === 0) return [];

    // Shuffle and return requested count
    const shuffled = allMedia.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, limitCount);
  } catch (error) {
    // Check if it's a Firebase index error and rethrow with additional info
    const indexError = parseFirebaseIndexError(error);
    if (indexError.isIndexError) {
      console.error('Firebase index error in getRandomMedia:', indexError);
      throw error; // Rethrow to be handled by caller
    }
    console.error('Error fetching random media:', error);
    return [];
  }
}

/**
 * Get media items for a specific month (YYYY-MM format)
 * Prioritizes images with capturedAt date matching the month, falls back to uploadedAt, then random
 * Uses simple queries to avoid composite index requirements
 * Only returns photos (not videos)
 * @param excludeIds - Optional array of media IDs to exclude (prevent duplicates across calendar)
 */
export async function getMediaForMonth(
  firestore: Firestore,
  yearMonth: string, // Format: 'YYYY-MM' e.g., '2025-01'
  limitCount: number = 1,
  excludeIds: string[] = []
): Promise<MediaItem[]> {
  const [year, month] = yearMonth.split('-').map(Number);
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59);

  const excludeSet = new Set(excludeIds);
  const mediaRef = collection(firestore, 'media');

  // Simple query - just get photos (NOT videos), then filter in memory
  try {
    const simpleQuery = query(
      mediaRef,
      where('type', '==', 'photo'),
      limit(100) // Get enough to find matches
    );

    const snapshot = await getDocs(simpleQuery);
    let allMedia = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MediaItem));

    // Filter by visibility in memory and exclude already-used images
    allMedia = allMedia.filter(m =>
      (m.visibility === 'public' || m.visibility === 'members') &&
      !excludeSet.has(m.id)
    );

    // First try to find images with capturedAt in this month
    const capturedMatches = allMedia.filter(m => {
      if (!m.capturedAt) return false;
      const capturedDate = m.capturedAt.toDate();
      return capturedDate >= startDate && capturedDate <= endDate;
    });

    if (capturedMatches.length > 0) {
      console.log(`Found ${capturedMatches.length} images with capturedAt for month ${yearMonth}`);
      return capturedMatches.slice(0, limitCount);
    }

    // Fall back to uploadedAt if no capturedAt matches
    const uploadedMatches = allMedia.filter(m => {
      if (!m.uploadedAt) return false;
      const uploadedDate = m.uploadedAt.toDate();
      return uploadedDate >= startDate && uploadedDate <= endDate;
    });

    if (uploadedMatches.length > 0) {
      console.log(`Found ${uploadedMatches.length} images with uploadedAt for month ${yearMonth}`);
      return uploadedMatches.slice(0, limitCount);
    }

  } catch (error) {
    console.error('Error querying images for month:', yearMonth, error);
  }

  // Final fallback: get random images from the gallery (excluding already-used)
  console.log(`No month-specific images for ${yearMonth}, using random images`);
  return getRandomMedia(firestore, limitCount, excludeIds);
}
